/* ============================================================================
 * เนื้อหาเอกสาร "นโยบายความเป็นส่วนตัว" / "ข้อกำหนดการใช้งาน" ที่พร้อมแสดงผล
 *
 * ที่มาของเนื้อหา (เรียงตามลำดับที่ใช้)
 *   1. ฐานข้อมูล — ผู้ดูแลแก้เองได้ที่ /ad/home/privacy และ /ad/home/terms
 *      อ่านผ่าน GET home/legal/:kind (เปิดสาธารณะ ไม่ต้องล็อกอิน)
 *   2. ไฟล์ในโค้ด (policy_option.js / terms_option.js) — ใช้เมื่อ API ล่มหรือ
 *      ฐานข้อมูลยังไม่มีเนื้อหา หน้านโยบายห้ามขึ้นหน้าว่างเด็ดขาด เพราะเป็น
 *      หลักฐานที่ LINE Developers Console ใช้ตรวจตอนขอสิทธิ์อีเมล
 *
 * ข้อมูลบริษัทในเนื้อหาเก็บเป็นคำแทน เช่น {company_name} {email} แล้วแทนค่าจริง
 * จาก "ตั้งค่า › ข้อมูลบริษัท" ตอนแสดงผล — แก้ที่หลังบ้านที่เดียวแล้วเอกสารเปลี่ยนตาม
 * เหมือนพฤติกรรมเดิมของ terms_option.js บรรทัดที่แทนแล้วว่าง (เช่นยังไม่ได้กรอก
 * สายด่วน) จะถูกตัดออก ไม่ปล่อยให้เอกสารมีหัวข้อที่ไม่มีค่า
 * ========================================================================== */
import { POLICY_CONTENT, POLICY_UPDATED } from "./policy_option.js";
import { termsCompany, termsContent, TERMS_UPDATED } from "./terms_option.js";

/* คำแทน -> ฟิลด์ของข้อมูลบริษัทที่ termsCompany() เตรียมไว้ให้ (เลือกภาษาแล้ว) */
const TOKEN_FIELDS = {
  "{company_name}": "name",
  "{brand}": "brand",
  "{website}": "website",
  "{email}": "email",
  "{phone}": "phone",
  "{address}": "address",
  "{hotline}": "hotline",
  "{line_oa}": "line_oa",
  "{license_no}": "license_no",
};

const fill = (value, company) => {
  let out = String(value === null || value === undefined ? "" : value);
  for (const [token, field] of Object.entries(TOKEN_FIELDS)) {
    if (out.includes(token)) out = out.split(token).join(company[field] || "");
  }
  return out.trim();
};

/* เขียนไว้แล้วแต่แทนค่าออกมาว่าง = ข้อมูลบริษัทช่องนั้นยังไม่ได้กรอก ให้ตัดทิ้ง
   ต่างจากช่องที่ผู้ดูแลตั้งใจเว้นว่างไว้เอง (เช่นรายการที่มีแต่หัวข้อย่อย ไม่มีคำอธิบาย) */
const emptied = (raw, filled) => !!String(raw === null || raw === undefined ? "" : raw).trim() && !filled;

/* แทนคำในบล็อกหนึ่งก้อน — คืน null ถ้าแทนแล้วไม่เหลือเนื้อหา (ข้อมูลบริษัทช่องนั้นว่าง)
   หน้าเว็บจะได้ไม่ขึ้นย่อหน้าเปล่าหรือหัวข้อที่ไม่มีค่าตามหลัง */
const fillBlock = (block, company) => {
  if (block?.type === "ul") {
    const items = (block.items || [])
      .map((item) => {
        const source = typeof item === "string" ? { label: "", text: item } : { label: item.label, text: item.text };
        const line = { label: fill(source.label, company), text: fill(source.text, company) };
        // "สายด่วน : {hotline}" ที่ยังไม่ได้กรอกสายด่วน ต้องหายทั้งบรรทัด ไม่ใช่เหลือหัวข้อลอย
        if (emptied(source.text, line.text) || emptied(source.label, line.label)) return null;
        return line.label || line.text ? line : null;
      })
      .filter(Boolean);
    return items.length ? { type: "ul", items } : null;
  }

  if (block?.type === "link") {
    const text = fill(block.text, company);
    return text ? { type: "link", href: block.href || "", text } : null;
  }

  const text = fill(block?.text, company);
  return text ? { type: block?.type === "note" ? "note" : "p", text } : null;
};

/* ------------------------------------------------------------ จากฐานข้อมูล */

/**
 * แปลงข้อมูลจาก API เป็นรูปที่หน้าเอกสารใช้ตรง ๆ (ชุดเดียวกับที่ไฟล์ในโค้ดคืน)
 * @param {Object} data - { doc: { th, en }, items: [...] } จาก GET home/legal/:kind
 * @param {"TH"|"EN"} lang
 * @param {Object} company - ข้อมูลจาก services/company.service.js
 */
const fromApi = (data, lang, company) => {
  const en = lang === "EN";
  const c = termsCompany(company, en ? "EN" : "TH");
  const head = (en ? data.doc?.en : data.doc?.th) || {};
  const fallbackHead = (en ? data.doc?.th : data.doc?.en) || {};

  // ฉบับที่ยังไม่ได้แปล ให้ใช้ของอีกภาษาไปก่อน ดีกว่าปล่อยหัวเอกสารว่าง
  const pick = (key) => fill(head[key] || fallbackHead[key], c);

  const sections = (data.items || [])
    .map((item) => {
      const title = fill(en ? item.title_en || item.title_th : item.title_th || item.title_en, c);
      const blocks = (en ? item.blocks_en : item.blocks_th) || [];
      const source = blocks.length ? blocks : (en ? item.blocks_th : item.blocks_en) || [];
      return {
        key: item.key || item.uid,
        title,
        highlight: !!item.highlight,
        blocks: source.map((block) => fillBlock(block, c)).filter(Boolean),
      };
    })
    .filter((section) => section.title);

  return {
    title: pick("title"),
    subtitle: pick("subtitle"),
    lead: pick("lead"),
    updated: head.updated || fallbackHead.updated || "",
    company: c,
    sections,
  };
};

/* --------------------------------------------------------- จากไฟล์ในโค้ด */

/** เนื้อหาสำรองเมื่อ API ใช้ไม่ได้ — รูปร่างเดียวกับ fromApi เป๊ะ */
const fromFile = (kind, lang, company) => {
  const en = lang === "EN";

  if (kind === "terms") {
    const content = termsContent(en ? "EN" : "TH", company);
    return {
      title: content.title,
      subtitle: content.subtitle,
      lead: content.lead,
      updated: (en ? TERMS_UPDATED.EN : TERMS_UPDATED.TH) || "",
      company: content.company,
      sections: content.sections.map((section) => ({
        key: section.key,
        title: section.title,
        highlight: !!section.highlight,
        blocks: section.blocks,
      })),
    };
  }

  const content = (en ? POLICY_CONTENT.EN : POLICY_CONTENT.TH) || POLICY_CONTENT.TH;
  return {
    title: content.title,
    subtitle: content.subtitle,
    lead: content.lead,
    updated: (en ? POLICY_UPDATED.EN : POLICY_UPDATED.TH) || "",
    company: termsCompany(company, en ? "EN" : "TH"),
    sections: content.sections.map((section) => ({
      key: section.key,
      title: section.title,
      highlight: !!section.highlight,
      blocks: section.blocks,
    })),
  };
};

/* ------------------------------------------------------------------ ทางเข้า */

/**
 * โหลดเนื้อหาเอกสารจากฐานข้อมูล
 *
 * ยิง fetch ตรงแทน GET() ของ services/api เพราะหน้านี้เป็นหน้าสาธารณะที่ผู้ชม
 * ไม่ได้ล็อกอิน — API ล่มแล้วควรตกไปใช้เนื้อหาในโค้ดเงียบ ๆ ไม่ใช่เด้งป๊อปอัปคาหน้า
 *
 * @param {"privacy"|"terms"} kind
 * @returns {Promise<Object|null>} null = ใช้ไม่ได้ ให้ผู้เรียกตกไปใช้ไฟล์ในโค้ด
 */
export const loadLegal = async (kind) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}home/legal/${kind}`, {
      method: "GET",
      headers: { accept: "application/json" },
    });
    const result = await response.json();
    if (result?.status === true && result.data?.items?.length) return result.data;
  } catch (e) {}

  return null;
};

/**
 * เนื้อหาฉบับที่พร้อมแสดงผล — มีข้อมูลจาก API ก็ใช้ ไม่มีก็ใช้ไฟล์ในโค้ด
 * @param {"privacy"|"terms"} kind
 * @param {"TH"|"EN"} lang
 * @param {Object} company
 * @param {Object|null} data - ผลจาก loadLegal()
 */
export const legalContent = (kind, lang, company, data) => {
  if (data?.items?.length) return fromApi(data, lang, company);
  return fromFile(kind, lang, company);
};
